/**
 * Build-time Feature Flags
 * ========================
 * Single source of truth for **build-time** feature flags — values
 * compiled into the bundle from `import.meta.env.VITE_*` at deploy.
 * Used for infra toggles that don't need to change without a redeploy.
 *
 * Runtime, user-targeted flags (PROPS_ENABLED, A/B tests, etc.) live in
 * `src/lib/featureFlags.ts` and evaluate through the PostHog browser
 * SDK. Don't add runtime flags here — they belong with PostHog so they
 * can be flipped without a deploy.
 *
 * KAN-120: Unifies the previous parallel system in `config/index.ts`'s
 * `features` field (liveBetting, aiPredictions, websockets, debugMode,
 * analytics) into this file so there's one place to look.
 *
 * KAN-123: Removed the six per-sport `PLAYER_PROPS_*_ENABLED` build-time
 * flags. They were dead via short-circuit (the master kill switch lived
 * in this file and short-circuited every per-sport check), and the
 * master kill switch has now moved to a runtime PostHog flag
 * (`PROPS_ENABLED`) — so per-sport build-time flags are doubly redundant.
 *
 * KAN-11809: Removed the sport-rollout flags (`MULTI_SPORT_ENABLED`,
 * `NFL_ENABLED`, `NCAAB_ENABLED`, `NBA_ENABLED`, `MLB_ENABLED`,
 * `NHL_ENABLED`) and the `isSportEnabled` / `getEnabledSports` helpers.
 * Sport availability now reads from `public.sport_config.is_active` via
 * the `useAvailableSportConfigs` hook in `src/hooks/useSports.ts`, which
 * matches the config-driven architecture mandate (adding a sport = DB
 * row insert, not a deploy). Don't reintroduce these as env flags.
 */

import React from 'react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseBoolean = (value: string | undefined, defaultValue: boolean = false): boolean => {
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
};

const isProductionLike = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname !== 'localhost' && hostname !== '127.0.0.1';
};

// ─── Feature flag interface ──────────────────────────────────────────────────

interface FeatureFlags {
  // Infra toggles (KAN-120: migrated from config/index.ts:features)
  // KAN-11809: sport-rollout flags removed — see file header.
  LIVE_BETTING_ENABLED: boolean;
  AI_PREDICTIONS_ENABLED: boolean;
  WEBSOCKETS_ENABLED: boolean;
  DEBUG_MODE_ENABLED: boolean;
  ANALYTICS_ENABLED: boolean;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Resolve build-time feature flags from `import.meta.env`. Defaults
 * mirror what `config/index.ts:features` shipped with before KAN-120 —
 * liveBetting/aiPredictions/websockets default ON, debugMode follows
 * dev-vs-prod, analytics on in production-like hosts.
 */
export const getFeatureFlags = (): FeatureFlags => {
  const fromEnv: FeatureFlags = {
    LIVE_BETTING_ENABLED: parseBoolean(import.meta.env.VITE_ENABLE_LIVE_BETTING, true),
    AI_PREDICTIONS_ENABLED: parseBoolean(import.meta.env.VITE_ENABLE_AI_PREDICTIONS, true),
    WEBSOCKETS_ENABLED: parseBoolean(import.meta.env.VITE_ENABLE_WEBSOCKETS, true),
    DEBUG_MODE_ENABLED: parseBoolean(import.meta.env.VITE_ENABLE_DEBUG_MODE, !!import.meta.env.DEV),
    ANALYTICS_ENABLED: parseBoolean(import.meta.env.VITE_ENABLE_ANALYTICS, isProductionLike()),
  };

  // Override with localStorage in dev (lets us flip flags without a rebuild
  // while iterating). Production reads `import.meta.env` only.
  if (import.meta.env.DEV) {
    try {
      const localFlags = localStorage.getItem('featureFlags');
      if (localFlags) {
        return { ...fromEnv, ...(JSON.parse(localFlags) as Partial<FeatureFlags>) };
      }
    } catch (e) {
      console.error('Error parsing feature flags from localStorage:', e);
    }
  }

  return fromEnv;
};

/**
 * Set feature flag overrides in localStorage (dev only).
 */
export const setFeatureFlags = (flags: Partial<FeatureFlags>) => {
  if (!import.meta.env.DEV) return;
  try {
    const current = getFeatureFlags();
    const updated = { ...current, ...flags };
    localStorage.setItem('featureFlags', JSON.stringify(updated));
    console.log('Feature flags updated:', updated);
    window.location.reload();
  } catch (e) {
    console.error('Error setting feature flags:', e);
  }
};

/**
 * JSX guard for build-time flags.
 */
export const FeatureFlag: React.FC<{
  flag: keyof FeatureFlags;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ flag, children, fallback = null }) => {
  const flags = getFeatureFlags();
  return flags[flag] ? <>{children}</> : <>{fallback}</>;
};

// Singleton instance
export const FEATURE_FLAGS = getFeatureFlags();
